import React, { Children } from "react";
import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import LcAnchorHeading from "@components/learningCenter/lcAnchorHeading";
import LcArticleBlock from "@components/learningCenter/lcArticleBlock";

import * as c from "./richTextOptions.module.scss";

export const createRichTextOptions = ({ noAnchorLinks = false } = {}) => ({
  renderNode: {
    [BLOCKS.HEADING_2]: (node, children) => {
      // Note: LC landing page styling depends on h2's getting an ID here
      // so if this ever needs to change, make sure to also update selector
      // in lcArticleLayout.module.scss
      return (
        <LcAnchorHeading level={2} node={node} noAnchorLinks={noAnchorLinks}>
          {children}
        </LcAnchorHeading>
      );
    },
    [BLOCKS.HEADING_3]: (node, children) => {
      return (
        <LcAnchorHeading level={3} node={node} noAnchorLinks={noAnchorLinks}>
          {children}
        </LcAnchorHeading>
      );
    },

    [BLOCKS.TABLE]: (node, children) => {
      const [tableHead, ...tableBody] = Children.toArray(children);

      return (
        <div className={c.tableScrollWrapper}>
          <table>
            <thead>{tableHead}</thead>
            <tbody>{tableBody}</tbody>
          </table>
        </div>
      );
    },
    [BLOCKS.TABLE_HEADER_CELL]: (node, children) => <th>{children}</th>,
    [BLOCKS.TABLE_ROW]: (node, children) => <tr>{children}</tr>,
    [BLOCKS.TABLE_CELL]: (node, children) => <td>{children}</td>,

    [BLOCKS.OL_LIST]: (node, children) => {
      const containsArticleBlock = !!children
        .flatMap((c1) => c1?.props?.children)
        .flatMap((c2) => c2?.props?.children)
        .find((c2) => c2?.type === LcArticleBlock);

      return <ol className={containsArticleBlock ? c.articleBlockList : undefined}>{children}</ol>;
    },

    [INLINES.HYPERLINK]: (node, children) => {
      const uri = node.data?.uri;
      const text = children;

      // Direct downloads from static folder, e.g. /downloads/charts.zip
      // Download attribute is required here since we want the link to
      // trigger a download in the same tab instead of navigating
      if (uri.startsWith("/downloads/")) {
        return (
          <a download href={uri}>
            {text}
          </a>
        );
      }

      // Fully qualified URLs should always open in a new tab/window
      // Gatsby URLs should always open in the same window in order to
      // increase speed of page load & preserve state.
      const protocols = ["https://", "http://", "www."];
      const isExternalUri = uri && protocols.some((p) => uri.includes(p));
      const target = isExternalUri ? "_blank" : "_self";

      return (
        <a target={target} href={uri}>
          {text}
        </a>
      );
    },

    [INLINES.ENTRY_HYPERLINK]: (node, children) => {
      const type = node?.data?.target?.__typename;

      switch (type) {
        case "ContentfulLcArticle": {
          const slug = node.data.target?.slug ?? "";

          return <Link to={`/learn/${slug}/`}>{children}</Link>;
        }

        case "ContentfulLcSection": {
          const section = node.data.target;

          return <Link to={`/learn/${section.slug}/`}>{children}</Link>;
        }

        case "ContentfulLcCategory": {
          const category = node.data.target;

          if (!category.lc_section) {
            console.warn("Can't render category link because 'lc_section' cannot be null. Category data:");
            console.warn(category);
            return <span>(PLACEHOLDER SEE ERROR LOG)</span>;
          }

          const section = category.lc_section[0];

          return <Link to={`/learn/${section.slug}/${category.slug}/`}>{children}</Link>;
        }

        case "ContentfulLcSubcategory": {
          const subcategory = node.data.target;

          if (!subcategory.lc_category) {
            console.warn("Can't render subcategory link because 'lc_category' cannot be null. Subcategory data:");
            console.warn(subcategory);
            return <span>(PLACEHOLDER SEE ERROR LOG)</span>;
          }

          const category = subcategory.lc_category[0];

          if (!category.lc_section) {
            console.warn(
              "Can't render subcategory link because 'lc_section' of category cannot be null. Subcategory data:"
            );
            console.warn(subcategory);
            return <span>(PLACEHOLDER SEE ERROR LOG)</span>;
          }

          const section = category.lc_section[0];

          return <Link to={`/learn/${section.slug}/${category.slug}/${subcategory.slug}/`}>{children}</Link>;
        }

        case undefined: {
          console.warn(`Undefined __typename on ENTRY_HYPERLINK`);
          return null;
        }

        default: {
          console.warn(`Unsupported hyperlink entry type: ${type}`);
          return null;
        }
      }
    },

    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const asset = node.data.target;
      let Component;

      if (asset?.file?.contentType === "video/mp4") {
        Component = (
          <div className={c.videoCtn}>
            <video autoPlay={true} muted={true} loop={true} playsInline={true}>
              <source src={asset.file?.url} />
            </video>
          </div>
        );
      } else if (asset?.gatsbyImageData) {
        Component = (
          <div className={c.imgCtn}>
            <GatsbyImage image={asset.gatsbyImageData} alt="" />
          </div>
        );
      }
      return Component;
    },
  },
});
